import React from 'react'

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

export interface AsyncOptions {
  /**
   * Opt-in background refresh: silently re-run every N ms, keeping the stale
   * data rendered — `loading` never flips, a failed refresh keeps the last
   * good result, and nothing runs while the tab is hidden. Consumers that do
   * not pass this behave exactly as before.
   */
  refreshEvery?: number
  /** Gate for the background refresh (e.g. "there are live rows"). Default true. */
  refreshActive?: boolean
}

/**
 * Runs an async read and re-runs it when `deps` change or `reload()` is called.
 * Late responses from a superseded run are discarded.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList,
  opts?: AsyncOptions,
): AsyncState<T> {
  const [data, setData] = React.useState<T | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [nonce, setNonce] = React.useState(0)

  // Keep the latest fn without making it a dependency — callers pass inline
  // closures and we don't want to refetch on every render.
  const fnRef = React.useRef(fn)
  React.useEffect(() => {
    fnRef.current = fn
  })

  React.useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)

    fnRef
      .current()
      .then((result) => {
        if (!alive) return
        setData(result)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (!alive) return
        setError(e instanceof Error ? e.message : String(e))
        setLoading(false)
      })

    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  // The silent background loop. Deliberately separate from the loading effect
  // above so the opt-in cannot change any existing consumer's semantics.
  const { refreshEvery, refreshActive = true } = opts ?? {}
  React.useEffect(() => {
    if (!refreshEvery || !refreshActive) return
    let alive = true
    let inFlight = false
    const id = window.setInterval(() => {
      if (inFlight || document.visibilityState !== 'visible') return
      inFlight = true
      fnRef
        .current()
        .then((result) => {
          if (alive) setData(result)
        })
        .catch(() => {
          /* transient — keep the stale data; the next interval retries */
        })
        .finally(() => {
          inFlight = false
        })
    }, refreshEvery)
    return () => {
      alive = false
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshEvery, refreshActive, nonce, ...deps])

  const reload = React.useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}
